import React, { useState, useEffect, useRef } from 'react';
import { Gallery, DisplaySettings } from '../../types/Gallery';
import { loadLiveCategories } from '../../utils/CategoryAPI';
import ImageCard from './ImageCard';
import GalleryModal from './GalleryModal';
import './RotatingGallery.css';

interface RotatingGalleryProps {
  galleries: Gallery[];
  settings: DisplaySettings;
}

const RotatingGallery: React.FC<RotatingGalleryProps> = ({ galleries, settings }) => {
  // Ensure galleryCount doesn't exceed actual available galleries
  const maxGalleries = settings.galleryCount || galleries.length;
  const actualGalleryCount = Math.min(maxGalleries, galleries.length);
  const displayGalleries = galleries.slice(0, actualGalleryCount);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleGalleryClick = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGallery(null);
  };

  // Load categories
  useEffect(() => {
    const loadCategoriesData = async () => {
      try {
        const categoriesData = await loadLiveCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategoriesData();

    const handleCategoryUpdate = () => {
      setTimeout(() => {
        loadCategoriesData();
      }, 100);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_categories') {
        setTimeout(() => {
          loadCategoriesData();
        }, 100);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('category_updated', handleCategoryUpdate);
    window.addEventListener('categories_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('category_updated', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Get category color
  const getCategoryColor = (categoryName: string): string => {
    const normalizedInput = (categoryName || '').toLowerCase().trim();
    const category = categories.find(cat =>
      (cat.name || '').toLowerCase().trim() === normalizedInput ||
      (cat.slug || '').toLowerCase().trim() === normalizedInput
    );
    return category?.color || '#8B4513';
  };

  // Apply theme settings
  useEffect(() => {
    const loadThemeAndApply = () => {
      const currentTheme = localStorage.getItem('admin-theme');
      if (currentTheme) {
        try {
          const themeData = JSON.parse(currentTheme);
          const root = document.documentElement;

          if (themeData.palette) {
            root.style.setProperty('--color-primary', themeData.palette.primary || '#8B4513');
            root.style.setProperty('--color-secondary', themeData.palette.secondary || '#A0522D');
            root.style.setProperty('--color-accent', themeData.palette.accent || '#D4AF37');
            root.style.setProperty('--color-background', themeData.palette.background || '#FFFFFF');
            root.style.setProperty('--color-text', themeData.palette.text || '#8B4513');
            root.style.setProperty('--color-surface', themeData.palette.surface || '#F8F9FA');
            root.style.setProperty('--color-border', themeData.palette.border || '#E1E1E1');
          }
        } catch (error) {
          console.error('Error loading theme settings:', error);
        }
      }
    };

    loadThemeAndApply();

    const handleThemeChange = () => {
      loadThemeAndApply();
    };

    window.addEventListener('storage', handleThemeChange);
    window.addEventListener('theme_updated', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('theme_updated', handleThemeChange);
    };
  }, []);

  // Auto-rotate functionality
  useEffect(() => {
    if (displayGalleries.length <= 1 || isPaused) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % displayGalleries.length);
    }, settings.rotationSpeed || 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [displayGalleries.length, isPaused, settings.rotationSpeed]);

  const nextGallery = () => {
    setCurrentIndex(prev => (prev + 1) % displayGalleries.length);
  };

  const prevGallery = () => {
    setCurrentIndex(prev => prev === 0 ? displayGalleries.length - 1 : prev - 1);
  };

  const goToGallery = (index: number) => {
    setCurrentIndex(index);
  };

  if (displayGalleries.length === 0) {
    return (
      <div className="rotating-gallery-container">
        <div className="no-galleries">
          <h2>Gallery Coming Soon</h2>
          <p>My rotating photography collection will be available shortly...</p>
        </div>
      </div>
    );
  }

  const currentGallery = displayGalleries[currentIndex];

  return (
    <>
      <div 
        className="rotating-gallery-container"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="rotating-viewport">
          <div 
            className="rotating-gallery-item"
            key={`${currentGallery.id}-${currentIndex}`}
          >
            <ImageCard 
              gallery={currentGallery}
              feature={true}
              showcase={true}
              className="rotating-card"
              onGalleryClick={handleGalleryClick}
              categoryColor={getCategoryColor(currentGallery.category)}
            />
          </div>

          {/* Navigation */}
          {displayGalleries.length > 1 && (
            <>
              <button 
                className="rotating-nav rotating-prev"
                onClick={prevGallery}
                aria-label="Previous gallery"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button 
                className="rotating-nav rotating-next"
                onClick={nextGallery}
                aria-label="Next gallery"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
        </div>

        {/* Thumbnail Navigation */}
        {displayGalleries.length > 1 && (
          <div className="rotating-thumbnails">
            {displayGalleries.map((gallery, index) => (
              <button
                key={gallery.id}
                className={`rotating-thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToGallery(index)}
                aria-label={`Go to ${gallery.name}`}
              >
                <img 
                  src={gallery.heroImage?.url || gallery.heroImage?.secure_url || ''} 
                  alt={gallery.name}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      <GalleryModal 
        gallery={selectedGallery}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default React.memo(RotatingGallery);
